import { ChangeEvent, FC, MouseEvent, useMemo, useState } from "react";
import isEqual from "react-fast-compare";
import { CreateClassRequest, Class } from "../../services/class/class.types";
import { ClassFormErrors, ClassFormValues } from "./ClassForm.types";
import UnsavedChangesModal from "../../common/UnsavedChangesModal/UnsavedChangesModal";
import {
  EditForm, EditFormActions, EditFormFields, FormFieldRow, FormRadioGroupContainer
} from "../Bases/EditForm.styles";
import { SubTitle } from "./ClassForm/ClassForm.styles";
import { InputWrapperLarge, InputWrapperMedium } from "../../common/FormElementWrappers/FormElementWrappers.styles";
import Input from "../../components/Input/Input";
import MaskedInput from "../../components/MaskedInput/MaskedInput";
import Dropdown from "../../components/Dropdown/Dropdown";
import { PRICE_OPTION, PriceOptionKeys } from "../../constants/ApiEnums";
import { OPTIONS_PLACEHOLDER } from "../../constants/Dropdown";
import RadioGroup from "../../components/RadioGroup/RadioGroup";
import { RadioGroupLayouts } from "../../components/RadioGroup/RadioGroup.types";
import ImageForm from "../../components/ImageForm/ImageForm";
import Button from "../../components/Button/Button";
import { readOnlyLabelTableText } from "../../constants/GlobalTexts";
import { statusOptions } from "../Bases/EditBase/StatusOptions";

interface EditClassFormProps {
  classData?: Class,
  submitForm: (classData: CreateClassRequest) => void,
  submitIsLoading: boolean
}

const ClassForm: FC<EditClassFormProps> = (
    {
      classData,
      submitForm,
      submitIsLoading
    }
) => {

  const initialFormValues = useMemo<ClassFormValues>(() => ({ //MERGE: Form type
    classId: classData?.classNo?.toString() ?? '',
    status: classData?.statusNo?.toString() ?? '',
    description: classData?.description ?? '',
    longDescription: classData?.longDescription ?? '',
    displayDescription: classData?.displayDesc ?? '',
    mnemonic: classData?.mnemonic ?? '',
    sequence: classData?.seqNo?.toString() ?? '',
    validOnWeb: classData?.validOnWeb ?? undefined,
    pcaClassNo: classData?.pcaClassNo?.toString() ?? '',
    priceOptionNo: classData?.priceOptionNo?.toString() ?? '',
    printInKitchen: classData?.printInKitchen ?? undefined,
    allowDiscount: classData?.allowDiscount ?? undefined,
    taxExempted: classData?.taxExempted ?? undefined,
    allowHalf: classData?.allowHalf ?? undefined
  }), [classData]);

  const [formValues, setFormValues] = useState<ClassFormValues>(initialFormValues);

  const [formErrors, setFormErrors] = useState<ClassFormErrors>({
    classId: '',
    status: '',
    description: '',
    longDescription: '',
    displayDescription: '',
    mnemonic: '',
    sequence: '',
    printInKitchen: '',
    allowDiscount: '',
    taxExempted: '',
    allowHalf: '',
    validOnWeb: '',
    pcaClassNo: '',
    priceOptionNo: ''
  });

  const unsavedChanges = !isEqual(initialFormValues, formValues);

  const updateFormValues = (
      event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    setFormErrors({
      ...formErrors,
      [event.target.name]: ''
    });
    setFormValues({
      ...formValues,
      [event.target.name]: event.target.value
    });
  }

  const updateFormValuesFromRadioGroup = (
      event: ChangeEvent<HTMLInputElement>
  ) => {
    setFormErrors({
      ...formErrors,
      [event.target.name]: ''
    });
    setFormValues({
      ...formValues,
      [event.target.name]: event.target.value.includes('Yes')
    });
  }

  const handleSave = (
      event: MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();

    if (formValuesHaveErrors()) {
      return;
    }

    submitForm(
        {
          classNo: +formValues.classId,
          statusNo: +formValues.status,
          description: formValues.description,
          longDescription: formValues.longDescription,
          displayDesc: formValues.displayDescription,
          mnemonic: formValues.mnemonic,
          seqNo: +formValues.sequence,
          validOnWeb: formValues.validOnWeb as boolean,
          pcaClassNo: +formValues.pcaClassNo,
          priceOptionNo: +formValues.priceOptionNo,
          printInKitchen: formValues.printInKitchen as boolean,
          allowDiscount: formValues.allowDiscount as boolean,
          taxExempted: formValues.taxExempted as boolean,
          allowHalf: formValues.allowHalf as boolean
        }
    );
  };

  const formValuesHaveErrors = () => {
    let error = false;
    let errorMessages: ClassFormErrors = {
      classId: '',
      displayDescription: '',
      mnemonic: '',
      longDescription: '',
      description: '',
      printInKitchen: '',
      allowDiscount: '',
      taxExempted: '',
      allowHalf: '',
      validOnWeb: '',
      status: '',
      pcaClassNo: '',
      priceOptionNo: '',
      sequence: ''
    };

    if ([null, undefined, -1, ''].includes(formValues.classId)) {
      errorMessages.classId = 'Class Number is required.';
      error = true;
    }

    if ([null, undefined, ''].includes(formValues.description)) {
      errorMessages.description = 'Short Description is required.';
      error = true;
    }

    if (
        [null, undefined, ''].includes(formValues.description)
        || formValues.description.length < 1
        || formValues.description.length > 50
    ) {
      errorMessages.description = 'Short Description must be 1-50 characters.';
      error = true;
    }

    if (
        formValues?.description != null
        && formValues.description !== ''
    ) {
      let i = 0;
      let upperCaseCharCnt = 0;
      let character;
      let description = formValues.description;
      /* Check to see if there are 2 uppercase characters in the description*/
      while (i <= description.length) {
        character = description.charAt(i);
        if (!isNaN(Number(character))) {
          //console.log(character + 'character is numeric');
        }
        else {
          if (character === character.toUpperCase()) {
            upperCaseCharCnt++;
          }
        }
        i++;
      }
      if (upperCaseCharCnt === 0) {
        error = true;
        errorMessages.description = 'There are no capital letters in the description';
      }

      if (upperCaseCharCnt > 2) {
        error = true;
        errorMessages.description = 'There must be 1 or 2 capital letters in the description';
      }
    }

    if (
        [null, undefined, ''].includes(formValues.longDescription)
        || formValues.longDescription.length < 1
        || formValues.longDescription.length > 30
    ) {
      errorMessages.longDescription = 'Long Description must be 1-30 characters.';
      error = true;
    }

    if ([null, undefined, -1, ''].includes(formValues.pcaClassNo)) {
      errorMessages.pcaClassNo = 'PCA Class Number is required.';
      error = true;
    }

    if ([null, undefined, -1, ''].includes(formValues.sequence)) {
      errorMessages.sequence = 'Sequence Number is required.';
      error = true;
    }

    if (
        [null, undefined, ''].includes(formValues.displayDescription)
        || formValues.displayDescription.length < 1
        || formValues.displayDescription.length > 9
    ) {
      errorMessages.displayDescription = 'BOH Description must be 1-9 characters.';
      error = true;
    }

    if (formValues.validOnWeb === undefined) {
      errorMessages.validOnWeb = 'Valid on Web is required.';
      error = true;
    }

    if (formValues.allowDiscount === undefined) {
      errorMessages.allowDiscount = 'Allow Discount is required.';
      error = true;
    }

    if (formValues.allowHalf === undefined) {
      errorMessages.allowHalf = 'Allow Half is required.';
      error = true;
    }

    if (formValues.printInKitchen === undefined) {
      errorMessages.printInKitchen = 'Print In Kitchen is required.';
      error = true;
    }

    if (formValues.taxExempted === undefined) {
      errorMessages.taxExempted = 'Taxable is required.';
      error = true;
    }

    if ([null, undefined, -1, ''].includes(formValues.priceOptionNo)) {
      errorMessages.priceOptionNo = 'Price Option Number is required.';
      error = true;
    }

    setFormErrors((formErrors) => {
      return { ...formErrors, ...errorMessages };
    });
    return error;
  };

  return (
      <EditForm>
        <EditFormFields>
          <FormFieldRow>
            <InputWrapperMedium>
              <MaskedInput
                  label={"Class Number"}
                  id={'classId'}
                  name={'classId'}
                  value={formValues.classId?.toString()}
                  onValueChange={(_values, source) => source.event && updateFormValues(source.event)}
                  isAllowed={(values) => +values.value < 2147483648}
                  errorText={formErrors?.classId}
                  disabled={!!classData}
              />
            </InputWrapperMedium>
            <InputWrapperMedium>
              <Dropdown
                  label={"Status"}
                  id={'status'}
                  name={'status'}
                  options={statusOptions}
                  value={formValues.status}
                  errorText={formErrors?.status}
                  onChange={updateFormValues}
              />
            </InputWrapperMedium>
          </FormFieldRow>
          <FormFieldRow>
            <InputWrapperLarge>
              <Input
                  label={"Short Description"}
                  id={'description'}
                  name={'description'}
                  value={formValues?.description}
                  onChange={updateFormValues}
                  errorText={formErrors?.description}
              />
            </InputWrapperLarge>
            <InputWrapperLarge>
              <Input
                  label={"Long Description"}
                  id={'longDescription'}
                  name={'longDescription'}
                  value={formValues?.longDescription}
                  onChange={updateFormValues}
                  errorText={formErrors?.longDescription}
              />
            </InputWrapperLarge>
          </FormFieldRow>
          <FormFieldRow>
            <InputWrapperMedium>
              <MaskedInput
                  label={"PCA Class Number"}
                  id={'pcaClassNo'}
                  name={'pcaClassNo'}
                  value={formValues.pcaClassNo?.toString()}
                  onValueChange={(_values, source) => source.event && updateFormValues(source.event)}
                  isAllowed={(values) => +values.value < 2147483648}
                  errorText={formErrors?.pcaClassNo}
              />
            </InputWrapperMedium>
            <InputWrapperLarge>
              <Dropdown
                  label={"Price Option Number"}
                  id={'priceOptionNo'}
                  name={'priceOptionNo'}
                  options={Object.keys(PRICE_OPTION).map((key: any) => {
                    return { value: key, label: `${key} - ${PRICE_OPTION[key as PriceOptionKeys]}` };
                  })}
                  value={formValues?.priceOptionNo?.toString() || OPTIONS_PLACEHOLDER}
                  errorText={formErrors?.priceOptionNo}
                  onChange={updateFormValues}
              />
            </InputWrapperLarge>
          </FormFieldRow>
          <FormFieldRow>
            <FormRadioGroupContainer>
              <RadioGroup
                  label={"Print In Kitchen?"}
                  id={'printInKitchen'}
                  options={[
                    {
                      label: 'Yes',
                      id: 'printInKitchenYes'
                    },
                    {
                      label: 'No',
                      id: 'printInKitchenNo'
                    }
                  ]}
                  layout={RadioGroupLayouts.Row}
                  selected={
                    formValues?.printInKitchen === true
                        ? 'printInKitchenYes'
                        : formValues?.printInKitchen === false
                            ? 'printInKitchenNo'
                            : undefined
                  }
                  onChange={updateFormValuesFromRadioGroup}
                  errorText={formErrors?.printInKitchen}
              />
            </FormRadioGroupContainer>
            <FormRadioGroupContainer>
              <RadioGroup
                  label={"Allow Discount?"}
                  id={'allowDiscount'}
                  options={[
                    {
                      label: 'Yes',
                      id: 'allowDiscountYes'
                    },
                    {
                      label: 'No',
                      id: 'allowDiscountNo'
                    }
                  ]}
                  layout={RadioGroupLayouts.Row}
                  selected={
                    formValues?.allowDiscount === true
                        ? 'allowDiscountYes'
                        : formValues?.allowDiscount === false
                            ? 'allowDiscountNo'
                            : undefined
                  }
                  onChange={updateFormValuesFromRadioGroup}
                  errorText={formErrors?.allowDiscount}
              />
            </FormRadioGroupContainer>
          </FormFieldRow>
          <FormFieldRow>
            <FormRadioGroupContainer>
              <RadioGroup
                  label={"Taxable?"}
                  id={'taxExempted'}
                  options={[
                    {
                      label: 'Yes',
                      id: 'taxExemptedYes'
                    },
                    {
                      label: 'No',
                      id: 'taxExemptedNo'
                    }
                  ]}
                  layout={RadioGroupLayouts.Row}
                  selected={
                    formValues?.taxExempted === true
                        ? 'taxExemptedYes'
                        : formValues?.taxExempted === false
                            ? 'taxExemptedNo'
                            : undefined
                  }
                  onChange={updateFormValuesFromRadioGroup}
                  errorText={formErrors?.taxExempted}
              />
            </FormRadioGroupContainer>
            <FormRadioGroupContainer>
              <RadioGroup
                  label={"Allow Half?"}
                  id={'allowHalf'}
                  options={[
                    {
                      label: 'Yes',
                      id: 'allowHalfYes'
                    },
                    {
                      label: 'No',
                      id: 'allowHalfNo'
                    }
                  ]}
                  layout={RadioGroupLayouts.Row}
                  selected={
                    formValues?.allowHalf === true
                        ? 'allowHalfYes'
                        : formValues?.allowHalf === false
                            ? 'allowHalfNo'
                            : undefined
                  }
                  onChange={updateFormValuesFromRadioGroup}
                  errorText={formErrors?.allowHalf}
              />
            </FormRadioGroupContainer>
          </FormFieldRow>
          <FormFieldRow>
            <InputWrapperMedium>
              <Input
                  label={"Mnemonic"}
                  id={'mnemonic'}
                  name={'mnemonic'}
                  value={formValues?.mnemonic}
                  disabled
              />
            </InputWrapperMedium>
            <InputWrapperMedium>
              <MaskedInput
                  label={"Sequence Number"}
                  id={'sequence'}
                  name={'sequence'}
                  value={formValues?.sequence?.toString()}
                  onValueChange={(_values, source) => source.event && updateFormValues(source.event)}
                  isAllowed={(values) => +values.value < 2147483648}
                  errorText={formErrors?.sequence}
              />
            </InputWrapperMedium>
          </FormFieldRow>
          <FormFieldRow>
            <FormRadioGroupContainer>
              <RadioGroup
                  label={"Valid on Web?"}
                  id={'validOnWeb'}
                  options={[
                    {
                      label: 'Yes',
                      id: 'validOnWebYes'
                    },
                    {
                      label: 'No',
                      id: 'validOnWebNo'
                    }
                  ]}
                  layout={RadioGroupLayouts.Row}
                  selected={
                    formValues?.validOnWeb === true
                        ? 'validOnWebYes'
                        : formValues?.validOnWeb === false
                            ? 'validOnWebNo'
                            : undefined
                  }
                  onChange={updateFormValuesFromRadioGroup}
                  errorText={formErrors?.validOnWeb}
              />
            </FormRadioGroupContainer>
            <InputWrapperLarge>
              <Input
                  label={"BOH Description"}
                  id={'displayDescription'}
                  name={'displayDescription'}
                  value={formValues?.displayDescription}
                  onChange={updateFormValues}
                  errorText={formErrors?.displayDescription}
              />
            </InputWrapperLarge>
          </FormFieldRow>
          {!!classData &&
            <ImageForm
              attribute={'clss'}
              resourceNo={[classData.classNo]}
            />
          }
          <SubTitle>{readOnlyLabelTableText}</SubTitle>
        </EditFormFields>
        <EditFormActions>
          <Button
              label={"Save"}
              onClick={handleSave}
              isLoading={submitIsLoading}
              disabled={!unsavedChanges}
          />
        </EditFormActions>
        <UnsavedChangesModal unsavedChangesExist={unsavedChanges}/>
      </EditForm>
  )
}

export default ClassForm;